import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Popover } from "antd";
import { IUserData } from "Data/interfaces/User/IUserData";
import { getUserInfo } from "Store/Auth/Auth.selector";
import { getUsersOptions } from "Store/Activities/Activities.selector";
import { Icon } from "Components/UI";
import SearchInput from "Components/UI/SearchInput";
import Button from "Components/UI/Button";
import EmptyFollowers from "./components/EmptyFollowers";
import CardUser from "./components/CardUser";
import { useQueryFollowers } from "Hooks/activities/useQueryFollowers";
import { useQueryAddFollowers } from "Hooks/activities/useQueryAddFollowers";
import { useQueryDeleteFollowers } from "Hooks/activities/useQueryDeleteFollowers";
import {
  ButtonStyled,
  FooterContent,
  HrContainer,
  Main,
  MainContent,
  Overlay,
  SearchContent,
} from "./styles";

interface IReportDropdown {
  topicId: string;
  assignedUsers: IUserData[];
}

const ReportDropdown: FC<IReportDropdown> = ({ topicId, assignedUsers }) => {
  const { t } = useTranslation('activities');
  const { data: followersUsers } = useQueryFollowers(topicId);
  const { addFollowers } = useQueryAddFollowers();
  const { deleteFollowers } = useQueryDeleteFollowers();

  const userInfo = useSelector(getUserInfo);
  const usersOptions = useSelector(getUsersOptions);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredFollowersUsers = useMemo(() => {
    if (search && followersUsers?.length > 0) {
      return followersUsers.filter(u => {
        return u?.Nome?.toLowerCase()?.includes(search.toLowerCase());
      });
    }
    return followersUsers;
  }, [followersUsers, search]);

  const filterAssigneds = useMemo(() => {
    return usersOptions?.filter(user => {
      return !followersUsers.some(u => u?.Id === user?.Id);
    });
  }, [usersOptions, followersUsers]);

  const filteredUserOptions = useMemo(() => {
    if (search && filterAssigneds?.length > 0) {
      return filterAssigneds.filter(u => {
        return u?.Nome?.toLowerCase()?.includes(search.toLowerCase());
      });
    }
    return filterAssigneds;
  }, [filterAssigneds, search]);

  const hasFollowers = followersUsers.length > 0;
  const hasAddUsers = filterAssigneds.length > 0;
  const hasSearchFollowers = filteredFollowersUsers.length > 0;
  const hasSearchAddUsers = filteredUserOptions.length > 0;
  const notHasSearchUser = search && !hasSearchFollowers && !hasSearchAddUsers;
  const youFollower = followersUsers?.some(user => userInfo?.Id === user?.Id);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const toggleFollowers = (userIds: IUserData[], add: boolean) => {
    if (userIds.length === 0) return;

    if (add) {
      addFollowers({ topicId, followers: userIds });
    } else {
      deleteFollowers({ topicId, followers: userIds.map(user => user.Id || '') });
    }
  }

  const overlay = (
    <Overlay>
      <SearchContent>
        <span className="searchWrapper">
          <SearchInput
            inputPlaceholder={t('filterPlaceholder.search')}
            inputClassName="searchInput"
            noDebounceValue={search}
            withDebounce={false}
            setSearch={setSearch}
          />
        </span>
      </SearchContent>
      <MainContent>
        {notHasSearchUser && (
          <EmptyFollowers text="Nenhum usuário encontrado" />
        )}
        {!notHasSearchUser && (
          <>
            {!hasFollowers && (
              <EmptyFollowers text="Nenhum usuário seguindo" />
            )}
            {hasFollowers && (
              <Main>
                <span className="mainTitle">
                  {`${followersUsers.length} usuário${followersUsers.length > 1 ? 's' : ''} seguindo`}
                </span>
                <div className="mainFollowers">
                  {filteredFollowersUsers.map(user => (
                    <CardUser
                      user={user}
                      isAssigned={assignedUsers?.some(u => u?.Id === user?.Id)}
                      isYou={userInfo?.Id === user?.Id}
                      isFollower
                      toggleFollowers={toggleFollowers}
                    />
                  ))}
                </div>
              </Main>
            )}
            <HrContainer><hr /></HrContainer>
            {!hasAddUsers && (
              <EmptyFollowers text="Nenhum usuário à adicionar" />
            )}
            {hasAddUsers && (
              <Main>
                <span className="mainTitle">
                  Adicionar seguidores à atividade
                </span>
                <div className="mainFollowers">
                  {filteredUserOptions?.map(user => (
                    <CardUser
                      user={user}
                      isAssigned={assignedUsers?.some(u => u?.Id === user?.Id)}
                      isYou={userInfo?.Id === user?.Id}
                      toggleFollowers={toggleFollowers}
                    />
                  ))}
                </div>
              </Main>
            )}
          </>
        )}
      </MainContent>
      <FooterContent youfollower={youFollower}>
        <Button
          type={youFollower ? 'dashed' : 'primary'}
          className="btnFollow"
          onClick={() => toggleFollowers([userInfo as IUserData], !youFollower)}
        >
          <Icon
            icon={youFollower ? 'notVisualizar' : 'visualizar'}
            customSize={12}
            className="btnFollowIcon"
          />
          <span className="btnFollowText">
            {`${youFollower ? 'Deixar de seguir' : 'Seguir'} atividade`}
          </span>
        </Button>
      </FooterContent>
    </Overlay>
  )

  return (
    <Popover
      content={overlay}
      placement="bottomRight"
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      overlayInnerStyle={{ padding: 0 }}
    >
      <ButtonStyled>
        <Icon
          icon="visualizar"
          customSize={12}
          className="preferencesOpenBtn"
        />
      </ButtonStyled>
    </Popover>
  )
}

export default ReportDropdown;
